/* eslint-disable react-hooks/rules-of-hooks */
import { Engine } from 'engine/Engine';
import { Entity } from 'engine/Entity';
import { ColliderFacet, ColliderType } from 'engine/facets/ColliderFacet';
import { Facet } from 'engine/facets/Facet';
import { KinematicBodyFacet } from 'engine/facets/KinematicBodyFacet';
import { SpriteFacet } from 'engine/facets/SpriteFacet';
import { SpriteGroupFacet } from 'engine/facets/SpriteGroupFacet';
import { StaticSpriteFacet } from 'engine/facets/StaticSpriteFacet';
import { AudioManager } from 'engine/lib/AudioManager';
import { emptyV2, newV2, scaleV2 } from 'engine/lib/V2';
import { create } from 'engine/lib/create';
import { Activatable } from 'game/behaviors/Activatable';
import { HealthBarBehavior } from 'game/behaviors/HealthBar/HealthBarBehavior';
import { Hittable } from 'game/behaviors/Hittable';
import { Laserable } from 'game/behaviors/Laserable';
import { Programmable } from 'game/behaviors/Programmable';
import { Waterable } from 'game/behaviors/Waterable';
import { CollisionLayer, MAX_BOT_HP } from 'game/config';
import { FILTER_OUTLINE_ID } from 'game/defs/FilterOutline';
import { nonEmpty } from 'game/lib/NonEmpty';
import { easeInQuart, easeOutBounce, easeOutQuart, lerp } from 'game/lib/easing';
import { rand } from 'game/lib/rand';
import { BotArmsSprite } from 'game/sprites/BotArmsSprite';
import { BotElectricity } from 'game/sprites/BotElectricitySprite';
import { BotEyesClosedSprite, BotEyesOpenSprite, BotEyesSadSprite } from 'game/sprites/BotEyesSprite';
import { BotShadow, BotSprite } from 'game/sprites/BotSprite';
import { BotState, emptyBotState } from 'game/state/BotState';
import { inGameUIClasses } from 'game/styles/inGameUI';
import { colors } from 'game/styles/theme';
import clamp from 'lodash/clamp';
import random from 'lodash/random';
import { createElement } from 'react';
import { BulletEntity } from './BulletEntity';
import { BlinkerBehavior } from './bot/BlinkerBehavior';
import { BotToolBehavior } from './bot/BotToolBehavior';
import { BotTooltipBehavior } from './bot/BotTooltipBehavior';

const botDeath = require('game/sfx/bot-death.mp3');
const electricity = require('game/sfx/electricity.mp3');
const ping = require('game/sfx/ping.mp3');

const botDeathAudio = new AudioManager(botDeath, { volume: 0.5 });
const electricityAudio = new AudioManager(electricity, { loop: true });
const pingAudio = new AudioManager(ping, { volume: 0.5 });

/* eslint-disable global-require */
const botHits = nonEmpty<AudioManager>([
  new AudioManager(require('game/sfx/bot-hit-01.mp3')),
  new AudioManager(require('game/sfx/bot-hit-02.mp3')),
  new AudioManager(require('game/sfx/bot-hit-03.mp3')),
  new AudioManager(require('game/sfx/bot-hit-04.mp3')),
]);
/* eslint-enable global-require */

interface BotBehaviorProps {
  sprite: SpriteGroupFacet,
  bodySprite: StaticSpriteFacet,
  armsSprite: StaticSpriteFacet,
  toolSprite: SpriteGroupFacet,
  eyesSprite: StaticSpriteFacet,
  electricitySprite: SpriteFacet,
  state: BotState,
  editBot: () => void,
  repair: (repairing: boolean) => void,
}

export class BotBehavior extends Facet<BotBehaviorProps> {

  tooltip = create(BotTooltipBehavior, { result: null });
  toolBehavior: BotToolBehavior | null = null;

  activatable: Activatable | null = null;
  programmable: Programmable = create(Programmable, { state: emptyBotState });
  botToolBehavior: BotToolBehavior | null = null;
  lastPosition = emptyV2;
  lastRunning = false;

  hitStart = -Infinity;
  hitVelocity = newV2();
  stunDuration = 500;

  deathStart: null | number = null;
  deathDuration = 200;

  reviveStart: null | number = null;

  registerHit = (hitter: Entity, engine: Engine) => {
    if (hitter instanceof BulletEntity) {
      rand(botHits).clone({ volume: random(0.25, 0.75) }).play();
      this.hitStart = engine.time.now;
      this.hitVelocity = hitter.props.velocity;
      this.props.state.hp -= hitter.props.damage;
      if (this.props.state.hp <= 0) {
        this.props.state.hp = 0;
        if (!this.props.state.didDie) {
          botDeathAudio.play();
          this.enterDeathState(engine);
        }
      }
    }
    return true;
  };

  enterDeathState(engine: Engine) {
    if (!this.deathStart) {
      this.props.state.didDie = true;
      this.deathStart = engine.time.now;
      this.activatable?.activate(false, engine);
      this.entity.getFacet(BlinkerBehavior)?.pause();
      this.props.eyesSprite.props.frame = BotEyesSadSprite(this.props.state);
    }
  }

  start(engine: Engine) {
    this.entity.transform.position = this.props.state.position;
    this.entity.addFacet(this.tooltip, engine);

    this.programmable.props.state = this.props.state;
    this.entity.addFacet(this.programmable, engine);

    const { sprite, armsSprite, toolSprite, state } = this.props;
    let text: StaticSpriteFacet | null = null;
    this.activatable = this.entity.addFacet(
      create(Activatable, {
        activate: active => {
          sprite.props.filter = active ? `url(#${FILTER_OUTLINE_ID})` : undefined;
          if (active) {
            text = create(StaticSpriteFacet, {
              frame: createElement('text', {
                className: inGameUIClasses.activatedText,
                y: -210,
              }, state.didDie ? '[f] Repair' : '[e] Edit'),
            });
            this.entity.addFacet(text, engine);
          } else if (text) {
            this.entity.removeFacet(text);
          }
        },
      }),
      engine,
    );

    this.botToolBehavior = this.entity.addFacet(
      create(BotToolBehavior, { armsSprite, sprite, toolSprite, state, result: null }),
      engine,
    );

    if (this.props.state.hp <= 0) {
      this.props.state.hp = 0;
      this.enterDeathState(engine);
    }
  }

  // eslint-disable-next-line complexity
  update(delta: number, engine: Engine): void {
    const { sprite, eyesSprite, state } = this.props;
    if (this.deathStart) {
      const deltaDeath = engine.time.now - this.deathStart;
      const deathProgress = clamp(deltaDeath / this.deathDuration, 1);
      sprite.transform.position[1] = lerp(0, 30, easeInQuart(deathProgress));
      const fadeStart = deltaDeath - 300;
      if (fadeStart >= 0) {
        const fadeDuration = 1200;
        const fadeProgress = clamp(fadeStart / fadeDuration, 1);
        if (fadeProgress <= 0.5) {
          eyesSprite.props.opacity = easeOutBounce(fadeProgress / 0.5);
        } else {
          eyesSprite.props.opacity = clamp((1 - fadeProgress) / 0.5, 0, 1);
        }
      }

      if (!engine.input.pressed.KeyF) this.props.repair(false);

      if (state.didDie && engine.input.pressed.KeyF && this.activatable?.active) {
        this.props.repair(true);
        state.hp += delta * 0.1;
        if (state.hp >= MAX_BOT_HP) {
          state.hp = MAX_BOT_HP;
          state.didDie = false;
          this.deathStart = null;
          this.reviveStart = engine.time.now;
          this.props.eyesSprite.props.frame = BotEyesOpenSprite(state);
          this.activatable.activate(false, engine);
          this.props.repair(false);
        }
      }
      return;
    }

    if (this.reviveStart) {
      const fadeDuration = 600;
      const fadeProgress = clamp((engine.time.now - this.reviveStart) / fadeDuration, 1);
      eyesSprite.props.opacity = easeOutBounce(fadeProgress);
      if (fadeProgress >= 0.5) {
        sprite.transform.position[1] = lerp(30, 0, easeOutQuart((fadeProgress - 0.5) * 2));
      }
      if (fadeProgress >= 0.6) {
        pingAudio.play();
      }
      if (fadeProgress < 1) {
        return;
      }
      this.reviveStart = null;
      this.entity.getFacet(BlinkerBehavior)?.play(engine);
    }

    if (engine.input.keyDown.KeyE && this.activatable?.active) {
      this.activatable.activate(false, engine);
      this.props.editBot();
    }

    if (state.electrifiedEnd) {
      const { position } = this.props.sprite.transform;
      if (engine.time.now <= state.electrifiedEnd) {
        this.props.electricitySprite.set(BotElectricity.on);
        this.entity.removeFacet(this.botToolBehavior!);
        electricityAudio.play();

        this.entity.getFacet(BlinkerBehavior)?.pause();
        this.props.eyesSprite.props.frame = BotEyesOpenSprite({
          ...state,
          palette: { ...state.palette, eyes: colors.white },
        });
        this.props.bodySprite.props.frame = BotSprite({
          palette: { ...state.palette, stroke: colors.white },
        });
        position[0] = position[0] === 2 ? -2 : 2;
        return;
      }
      this.entity.getFacet(BlinkerBehavior)?.play(engine);
      this.props.eyesSprite.props.frame = BotEyesOpenSprite(state);
      this.props.bodySprite.props.frame = BotSprite(state);
      this.props.electricitySprite.set(BotElectricity.off);
      this.entity.addFacet(this.botToolBehavior!, engine);
      electricityAudio.pause();
      position[0] = 0;
      state.electrifiedEnd = null;
    }

    this.tooltip.props.result = this.programmable.result;
    this.botToolBehavior!.props.result = this.programmable.result;

    if (this.lastPosition[0] > this.entity.transform.position[0]) {
      sprite.transform.scale[0] = 1;
    } else if (this.lastPosition[0] < this.entity.transform.position[0]) {
      sprite.transform.scale[0] = -1;
    }
    this.lastPosition = [...this.entity.transform.position];
    if (this.lastRunning && !this.props.state.running) {
      this.props.state.cache = {};
    }
    this.lastRunning = this.props.state.running;
  }

  physicsUpdate(delta: number, engine: Engine): void {
    if (!engine.time.timeScale || this.reviveStart || this.props.state.electrifiedEnd) return;

    const deltaHit = engine.time.now - this.hitStart;
    if (deltaHit <= this.stunDuration) {
      const speed = 0.3 - lerp(0, 0.3, easeOutQuart(deltaHit / this.stunDuration));
      this.entity.getFacet(KinematicBodyFacet)?.move(scaleV2([...this.hitVelocity], speed * delta), engine);
      return;
    }
    this.programmable.step(delta, engine);
  }

  stop() {
    electricityAudio.stop();
  }
}

interface BotEntityProps {
  editBot: () => void,
  repair: (repairing: boolean) => void,
  state: BotState,
}

export class BotEntity extends Entity<BotEntityProps> {
  createFacets(props: BotEntityProps) {
    const eyesContainer = create(StaticSpriteFacet, { frame: null, offset: [-31, -154] });
    const armsSprite = create(StaticSpriteFacet, { frame: BotArmsSprite, offset: [-35, -104] });
    const toolSprite = create(SpriteGroupFacet, { children: [] });
    const bodySprite = create(StaticSpriteFacet, { frame: BotSprite(props.state), offset: [-62, -186] });
    const sprite = create(SpriteGroupFacet, {
      children: [
        bodySprite,
        eyesContainer,
        armsSprite,
      ],
    });
    const electricitySprite = create(SpriteFacet, { frameset: BotElectricity.off, offset: [-170, -290] });

    const hitbox = create(ColliderFacet, {
      type: ColliderType.Circle,
      offset: [-64, -170],
      radius: 64,
      trigger: true,
      layer: CollisionLayer.Hitbox,
    });
    const behavior = create(BotBehavior, {
      sprite,
      bodySprite,
      armsSprite,
      toolSprite,
      eyesSprite: eyesContainer,
      electricitySprite,
      editBot: props.editBot,
      repair: props.repair,
      state: props.state,
    });

    return [
      behavior,
      create(BlinkerBehavior, {
        frameOpen: BotEyesOpenSprite(props.state),
        frameOpenRange: [1000, 5000],
        frameClosed: BotEyesClosedSprite(props.state),
        frameClosedRange: [500, 1000],
        sprite: eyesContainer,
      }),
      create(StaticSpriteFacet, { frame: BotShadow, offset: [-62, -186] }),
      electricitySprite,
      sprite,
      toolSprite,
      create(ColliderFacet, { type: ColliderType.Box, offset: [-48, -12], size: [100, 28] }),
      create(ColliderFacet, { type: ColliderType.Circle, offset: [-62, -12], radius: 14 }),
      create(ColliderFacet, { type: ColliderType.Circle, offset: [38, -12], radius: 14 }),
      hitbox,
      create(KinematicBodyFacet, {}),
      create(Hittable, { hit: behavior.registerHit }),
      create(Waterable, {
        maxDistance: 128,
        offset: () => emptyV2,
        amountNeeded: () => 0.1,
        water: (amount, engine) => {
          const electrifiedDuration = 1000;
          props.state.electrifiedEnd = engine.time.now + electrifiedDuration;
          return false;
        },
      }),
      create(Laserable, { didSucceed: () => props.state.didDie }),
      create(HealthBarBehavior, {
        getHp: () => props.state.hp,
        maxHp: MAX_BOT_HP,
        width: 120,
        offset: [0, -260],
      }),
    ];
  }
}
